import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import fitbitAuthService from "../../services/FitbitAuthService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { Spin } from "antd";



/**
 * Fitbit OAuth Callback Handler Page
 * This page handles the redirect from Fitbit after user authorization
 */
const FitbitCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasCalledAPI = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (hasCalledAPI.current) return;
      hasCalledAPI.current = true;

      // Clear search parameters from URL immediately to prevent other components from reacting
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Handle authorization error
      if (error) {
        if (window.opener) {
          window.opener.postMessage(
            { type: "FITBIT_ERROR", error },
            window.location.origin
          );
          window.close();
        } else {
          navigate(
            `/glucera${GluceraNavigationRoutes.fitbitConnectionPage}`,
            {
              state: { error: `Fitbit authorization failed: ${error}` },
            }
          );
        }
        return;
      }

      // Handle success with code
      if (code && state) {
        try {
          // If opened in popup, send message to parent and close
          if (window.opener) {
            window.opener.postMessage(
              {
                type: "FITBIT_CALLBACK",
                code,
                state,
                success: true,
              },
              window.location.origin
            );

            // Short delay to ensure message is sent before closing
            setTimeout(() => {
              window.close();
            }, 500);
          } else {
            // If opened in main window, exchange code first then navigate
            await fitbitAuthService.exchangeCode(code, state);
            // Navigate to fitness page with success message
            navigate(`/glucera${GluceraNavigationRoutes.FITNESS}`, {
              state: { message: "Fitbit connected successfully!" },
            });
          }
        } catch (error) {
          console.error("[Fitbit Callback] Error:", error);
          if (window.opener) {
            window.opener.postMessage(
              { type: "FITBIT_ERROR", error: "Failed to connect Fitbit" },
              window.location.origin
            );
            window.close();
          } else {
            navigate(`/glucera${GluceraNavigationRoutes.fitbitConnectionPage}`, {
              state: { error: "Failed to connect Fitbit. Please try again." },
            });
          }
        }
      } else {
        // Missing required parameters
        navigate(`/glucera${GluceraNavigationRoutes.fitbitConnectionPage}`, {
          state: { error: "Invalid callback parameters" },
        });
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <Spin size="large" />
        <p className="mt-4 text-title3 text-foreground">
          Connecting Fitbit...
        </p>
      </div>
    </div>
  );
};

export default FitbitCallbackPage;
